import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import Button from './Button'
import moment from 'moment'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  defaultValue?: moment.Moment
  handleChange?: (yearMonth: moment.Moment) => void
}

export const YearMonthPicker = ({ defaultValue, handleChange }: IProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState(defaultValue || moment())
  const [displayYear, setDisplayYear] = useState((defaultValue || moment()).year())
  const containerRef = useRef(null)

  const months = [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const selectMonth = idx => {
    const newDate = moment({ year: displayYear, month: idx })
    setSelected(newDate)
    if (handleChange) {
      handleChange(newDate)
    }
    setIsOpen(false)
  }

  return (
    <Container ref={containerRef}>
      <Input onClick={() => setIsOpen(!isOpen)}>
        {selected.year()}年 {selected.month() + 1}月
      </Input>

      {isOpen && (
        <Popover>
          <YearHeader>
            <Button handleClick={() => setDisplayYear(displayYear - 1)} small={true}>
              <SvgIcon name="chevron_left" className="icon" />
            </Button>
            <span>{displayYear}年</span>
            <Button handleClick={() => setDisplayYear(displayYear + 1)} small={true}>
              <SvgIcon name="chevron_right" className="icon" />
            </Button>
          </YearHeader>
          <Grid>
            {months.map((m, i) => (
              <MonthButton
                key={m}
                active={selected.year() === displayYear && selected.month() === i}
                onClick={() => selectMonth(i)}
                aria-label={`${displayYear}年 ${m}月`}
              >
                {m}
              </MonthButton>
            ))}
          </Grid>
        </Popover>
      )}
    </Container>
  )
}
const Container = styled.div`
  position: relative;
  display: inline-block;
  font-family: sans-serif;
`

const Input = styled.div`
  padding: 8px 12px;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 120px;
  background: white;
`

const Popover = styled.div`
  position: absolute;
  top: 45px;
  left: 0;
  background: white;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 4px;

  box-shadow: ${constants.COLORS.cardShadow};
  padding: 12px;
  z-index: 100;
  width: 250px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 8px;
`

const MonthButton = styled.button`
  padding: 4px 8px;
  background: ${props => (props.active ? constants.COLORS.primary : constants.COLORS.white)};
  color: ${props => (props.active ? constants.COLORS.white : constants.COLORS.subText)};
  border: none;
  cursor: pointer;
  &:hover {
    border-color: ${constants.COLORS.primary};
  }
`

const YearHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  margin-bottom: 10px;

  .icon {
    width: 16px;
    height: 16px;
  }
`
